import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs";
import { AttachmentUtils } from "../../core/utils/attachment-utils";
import { MaiaGeocodeService } from "../../data-access-layer/maia/maia-geocode.service";
import { MaiaScreen } from "../../model/maia/maia-wizard-progress";
import {
    AttentionColumn,
    AttentionRow,
    GeocodeEvent,
    GeocodeFailedLocation,
    GeocodeStatus,
    GeocodeSummary,
} from "../../model/maia/sov-geocode";


/**
 * MAIA wizard — Geocode (macro step "Verify Locations", micro "Step 2 of 4").
 *
 * `status` drives which screen shows:
 *   IN_PROGRESS — live progress while the job runs
 *   COMPLETE    — the job finished; `summary.failed` empty = every location
 *                 resolved, non-empty = "needs attention" with a per-row fix
 *                 table. Both are the same status, not separate ones.
 *   FAILED      — the job itself errored (exception/crash), not the same as
 *                 a location needing attention.
 *
 * Backed by MaiaGeocodeService, which calls the real geocode endpoint and
 * SSE stream (see that file for the confirmed wire contract). `retryLocations`
 * is still a simulation — no per-location retry endpoint was given. Any
 * non-empty edit is treated as resolved; once every failure clears,
 * `summary.failed` is empty and the success screen shows.
 */
@Component({
    selector: "maia-geocode-step",
    templateUrl: "./maia-geocode-step.component.html",
    styleUrls: ["./maia-geocode-step.component.scss"],
})
export class MaiaGeocodeStepComponent implements OnChanges, OnDestroy {
    @Input() jobId: number | null = null;
    @Input() fileName = "";
    @Input() totalLocations = 0;
    /** false when returning to an already-geocoded job (e.g. Back from enrichment) - don't re-run. */
    @Input() autoStart = true;

    private loadedOnce = false;

    /** The card's close (×) control — a geocode job in flight always counts as unsaved progress. */
    @Output() readonly closeRequested = new EventEmitter<boolean>();

    /** "Download" / "Download to Review or Edit" — no export endpoint given yet. */
    @Output() readonly downloadRequested = new EventEmitter<void>();

    /**
     * The corrected workbook coming back after the user edited it offline.
     * Pairs with downloadRequested: download, fix in bulk, re-upload.
     * No endpoint given yet — see saveRows.
     */
    @Output() readonly correctedFileSelected = new EventEmitter<File>();

    /** "Continue to Enrichment" / "Continue without fixing" — Step 3 isn't built yet. */
    @Output() readonly continueRequested = new EventEmitter<void>();

    /**
     * Fires whenever the internal status settles into one worth recording as
     * the job's `currentScreen` for resume-on-refresh (see MaiaPageComponent's
     * `onGeocodeScreenChanged`) — screen keys from maia-wizard-progress.ts.
     */
    @Output() readonly screenChanged = new EventEmitter<string>();

    readonly GeocodeStatus = GeocodeStatus;

    status: GeocodeStatus = GeocodeStatus.IN_PROGRESS;
    /** Client-computed percent while IN_PROGRESS — see MaiaGeocodeService. */
    progressPercent = 0;
    summary: GeocodeSummary | null = null;

    /** Dynamic columns of the attention table, derived from the API's rawData keys. */
    attentionColumns: AttentionColumn[] = [];
    /** Editable rows behind the attention table. */
    attentionRows: AttentionRow[] = [];
    /** True while GET /geocode/not-found is in flight. */
    isLoadingAttention = false;
    /** Set when the not-found list itself could not be fetched. */
    attentionError = "";
    /** Only meaningful when status === FAILED. */
    errorMessage = "";


    /** Same formats the SOV upload accepts, since this is the same workbook returning. */
    readonly acceptedExtensions = ".csv,.xlsx,.xls,.json";

    /** Name of the corrected file the user picked, echoed back as confirmation. */
    correctedFileName = "";

    /** True while the export is being generated. */
    isDownloading = false;
    /** Set when the export request failed. */
    downloadError = "";

    /** True while an Update / Update now request is in flight. */
    isSavingRows = false;
    /** Set when an update request failed. */
    updateError = "";

    /** True while the corrected workbook is being sent back. */
    isImporting = false;
    /** Set when the import failed. */
    importError = "";
    /** Confirmation shown after a successful import. */
    importMessage = "";

    @ViewChild("correctedFileInput")
    private readonly correctedFileInput?: ElementRef<HTMLInputElement>;

    private jobSubscription?: Subscription;
    private attentionSubscription?: Subscription;
    private countersSubscription?: Subscription;
    private downloadSubscription?: Subscription;
    private importSubscription?: Subscription;
    private updateSubscription?: Subscription;

    constructor(private readonly geocodeService: MaiaGeocodeService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.totalLocations && !this.jobSubscription && !this.loadedOnce) {
            this.loadedOnce = true;
            if (this.autoStart) {
                this.startGeocode();
            } else {
                this.loadCompletedState();
            }
        }
    }

    /**
     * Returning to an already-geocoded job (Back from enrichment / resume): show the completed
     * screen without re-running geocoding. The stream-built summary is gone, so counts are
     * reconstructed from the not-found list (total minus failed).
     */
    private loadCompletedState(): void {
        const jobId = this.jobId;
        if (jobId === null) {
            return;
        }
        // Same two sources as a live completion: counters for the labels, the
        // not-found list for the rows and the screen. This used to synthesise
        // a summary instead (preserved hard-coded to 0, geocoded derived as
        // total minus failed), so a refresh showed different numbers from the
        // run that produced them.
        //
        // status is set by the counters callback, not here - see
        // showCompleteWhenCountersLand for why.
        this.showCompleteWhenCountersLand(jobId);
        this.loadAttentionRows(jobId, true);
    }

    ngOnDestroy(): void {
        this.stopJob();
        if (this.attentionSubscription) {
            this.attentionSubscription.unsubscribe();
        }
        if (this.downloadSubscription) {
            this.downloadSubscription.unsubscribe();
        }
        if (this.importSubscription) {
            this.importSubscription.unsubscribe();
        }
        if (this.updateSubscription) {
            this.updateSubscription.unsubscribe();
        }
        if (this.countersSubscription) {
            this.countersSubscription.unsubscribe();
        }
    }

    requestClose(): void {
        this.closeRequested.emit(true);
    }

    /** Whether every location resolved — the "all good" vs "needs attention" split within COMPLETE. */
    get allResolved(): boolean {
        return !!this.summary && this.summary.failedCount === 0;
    }

    findOnMap(location: GeocodeFailedLocation): void {
        const query = encodeURIComponent(location.address);
        window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank", "noopener");
    }



    get hasEditedLocation(): boolean {
        return this.visibleAttentionRows.some((row) => this.isRowEdited(row));
    }

    /** How many rows are actually editable inline. */
    readonly maxEditableRows = MaiaGeocodeService.MAX_EDITABLE_ROWS;

    /**
     * The rows the table renders — all of them, or none at all.
     *
     * Past the limit the inline table stops being a sane way to fix data, so
     * the screen drops it entirely and offers only the download/upload round
     * trip. Returning [] rather than just hiding the markup is what keeps
     * `editedRows`, `hasEditedLocation` and the footer buttons agreeing with
     * what is actually on screen — there is nothing to retry when there is
     * nothing to type into.
     */
    get visibleAttentionRows(): AttentionRow[] {
        return this.exceedsEditableLimit ? [] : this.attentionRows;
    }

    /** True when there are more failures than the table is willing to show. */
    get exceedsEditableLimit(): boolean {
        return this.attentionRows.length > this.maxEditableRows;
    }

    /**
     * "Download to Review or Edit" only becomes meaningful once the failures
     * outgrow the table; until then everything is fixable inline, so the
     * button stays disabled.
     */
    get canDownloadForReview(): boolean {
        return this.exceedsEditableLimit;
    }

    /**
     * Downloads the not-found rows as a workbook, for the failures that don't
     * fit the inline table.
     */
    downloadNotFound(): void {
        if (!this.canDownloadForReview || this.isDownloading || this.jobId === null) {
            return;
        }

        const jobId = this.jobId;
        this.isDownloading = true;
        this.downloadError = "";

        this.downloadSubscription = this.geocodeService.exportNotFoundLocations(jobId).subscribe({
            next: (response) => {
                this.isDownloading = false;
                if (!response.body) {
                    this.downloadError = "The export came back empty. Please try again.";
                    return;
                }
                AttachmentUtils.downloadFileWithName(
                    response.body as any,
                    this.geocodeService.resolveExportFileName(response, jobId)
                );
            },
            error: () => {
                this.isDownloading = false;
                this.downloadError = "We couldn't prepare the download. Please try again.";
            },
        });
    }

    /**
     * Opens the picker for the corrected workbook. Gated with the download for
     * the same reason: below the inline limit there is nothing to download, so
     * there is nothing to bring back either.
     */
    openCorrectedFilePicker(): void {
        if (!this.canDownloadForReview) {
            return;
        }
        if (this.correctedFileInput) {
            this.correctedFileInput.nativeElement.click();
        }
    }

    onCorrectedFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files.length ? input.files[0] : null;

        // Lets the same file be re-picked straight after a failed attempt.
        input.value = "";

        if (!file) {
            return;
        }

        this.correctedFileName = file.name;
        this.correctedFileSelected.emit(file);
        this.importCorrectedFile(file);
    }

    /**
     * Sends the corrected workbook back, then reloads the not-found list.
     *
     * The list is reloaded rather than the response being trusted to describe
     * the new state: whatever the import returns, the authoritative answer to
     * "what still needs attention" is that endpoint.
     */
    private importCorrectedFile(file: File): void {
        if (this.jobId === null) {
            return;
        }

        this.isImporting = true;
        this.importError = "";
        this.importMessage = "";

        this.importSubscription = this.geocodeService
            .importCorrectedLocations(this.jobId, file)
            .subscribe({
                next: () => {
                    this.isImporting = false;
                    this.importMessage = `${file.name} imported.`;
                    if (this.jobId !== null) {
                        this.loadCounters(this.jobId);
                        this.loadAttentionRows(this.jobId);
                    }
                },
                error: (error) => {
                    this.isImporting = false;
                    this.importError = this.extractHttpMessage(
                        error,
                        "We couldn't import that file. Please check it and try again."
                    );
                },
            });
    }

    /**
     * Checks every row's Coordinates cell, tagging the bad ones so the table
     * can point at them. Returns true when all rows are good.
     */
    private validateCoordinates(rows: AttentionRow[]): boolean {
        let allValid = true;

        for (const row of rows) {
            const parsed = this.geocodeService.parseCoordinates(
                row.values[this.coordinateKey] || ""
            );
            row.error = parsed.error;
            if (parsed.error) {
                allValid = false;
            }
        }

        return allValid;
    }

    /** Clears a row's coordinate error as soon as the user edits it again. */
    onCoordinateInput(row: AttentionRow): void {
        if (row.error) {
            row.error = null;
        }
        if (this.updateError) {
            this.updateError = "";
        }
    }

    /** Pulls a readable message off an HttpErrorResponse body. */
    private extractHttpMessage(error: any, fallback: string): string {
        const body = error && error.error;
        if (typeof body === "string" && body.trim()) {
            return body;
        }
        if (body && typeof body.message === "string" && body.message.trim()) {
            return body.message;
        }
        if (body && typeof body.error === "string" && body.error.trim()) {
            return body.error;
        }
        return fallback;
    }

    /**
     * Opens Google Maps for a row: the coordinate pair when it has one,
     * otherwise the address fields ordered specific-to-general so Maps can
     * actually resolve them.
     */
    findOnMapRow(row: AttentionRow): void {
        // Ordering matters — see MaiaGeocodeService.QUERY_PART_ORDER.
        const query = this.geocodeService.buildMapQuery(this.attentionColumns, row);

        if (!query) {
            return;
        }
        window.open(
            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
            "_blank",
            "noopener"
        );
    }

    /**
     * CSS grid template for the attention table: one column per dynamic field,
     * then the action column. Built here because the number of columns is only
     * known at runtime.
     */
    get gridTemplate(): string {
        // Data columns have a floor so they stay readable; once the floors add
        // up past the panel the grid overflows and .attention-scroll takes over
        // rather than squeezing the inputs.
        const dynamic = this.attentionColumns.map(() => "minmax(150px, 1fr)").join(" ");
        // Action is fixed and wide enough for Update + Find on map side by
        // side — too narrow and the buttons overflow the cell, which the
        // table's overflow:hidden (for the corner radius) then clips.
        return `${dynamic} 250px`;
    }

    /** True once any of this row's values differs from what the API returned. */
    isRowEdited(row: AttentionRow): boolean {
        return this.attentionColumns.some(
            (column) => (row.values[column.key] || "") !== (row.originalValues[column.key] || "")
        );
    }

    get editedRows(): AttentionRow[] {
        return this.visibleAttentionRows.filter((row) => this.isRowEdited(row));
    }

    /** Column key for the coordinate pair — see MaiaGeocodeService. */
    private readonly coordinateKey = MaiaGeocodeService.COORDINATE_KEY;

    /** Per-row "Update" in the Action column. */
    updateRow(row: AttentionRow): void {
        this.saveRows([row]);
    }

    /**
     * Footer "Retry now" — sends the complete list, not only the edited rows,
     * so the server receives the full current state in one call.
     *
     * `visibleAttentionRows`, not `attentionRows`: past the editable limit the
     * table is gone and nothing on screen was edited, so this must not quietly
     * resubmit a list the user never saw.
     */
    updateAllEdited(): void {
        this.saveRows(this.visibleAttentionRows);
    }

    /**
     * Fetches the rows needing attention. Called once the geocode stream
     * reaches COMPLETE — the SSE frames carry only aggregate counts, so this
     * is the only source of per-row detail.
     */
    /**
     * Preserved / Geocoded / Failed, read from the job's server-side counters.
     *
     * On failure the labels are left blank rather than shown as zeros - a
     * wrong number reads as fact, a missing one reads as missing.
     *
     * `onSettled` runs whether the call succeeded or not, and is how the
     * completion paths wait for this before showing a COMPLETE screen - see
     * showCompleteWhenCountersLand. The refresh paths (Retry, import) pass
     * nothing: they already have a screen on display and only want the
     * numbers on it updated.
     */
    private loadCounters(jobId: number, onSettled?: () => void): void {
        if (this.countersSubscription) {
            this.countersSubscription.unsubscribe();
        }
        this.countersSubscription = this.geocodeService.getJobSummary(jobId).subscribe({
            next: (summary) => {
                this.summary = this.geocodeService.buildSummaryFromCounters(
                    summary,
                    this.totalLocations
                );
                if (onSettled) {
                    onSettled();
                }
            },
            error: () => {
                this.summary = null;
                // Still settled: a missing summary must not strand the user on
                // the progress screen. allResolved is false without one, so
                // this lands on needs-attention - the same fallback this
                // screen already uses when the counters can't be read.
                if (onSettled) {
                    onSettled();
                }
            },
        });
    }

    /**
     * Moves to the COMPLETE screen once the counters that decide which COMPLETE
     * screen it is have arrived.
     *
     * `allResolved` is read off `summary` and nothing else, so flipping status
     * the moment the job finishes makes the template choose its branch from a
     * summary that is still null - which reads as "not all resolved" and paints
     * the needs-attention screen for a fraction of a second on every job,
     * including ones with zero failures. Waiting for that one request removes
     * it; the progress screen stays up meanwhile, which is true - the job has
     * finished, we just cannot yet say how it went.
     *
     * The rows list is deliberately NOT waited on. It only fills the table
     * inside the needs-attention branch, which has its own spinner for exactly
     * this window, so waiting for it would delay the screen for nothing.
     */
    private showCompleteWhenCountersLand(jobId: number): void {
        this.loadCounters(jobId, () => {
            this.status = GeocodeStatus.COMPLETE;
        });
    }

    private loadAttentionRows(jobId: number, emitScreen = false): void {
        this.isLoadingAttention = true;
        this.attentionError = "";

        if (this.attentionSubscription) {
            this.attentionSubscription.unsubscribe();
        }

        this.attentionSubscription = this.geocodeService.getNotFoundLocations(jobId).subscribe({
            next: (locations) => {
                this.isLoadingAttention = false;
                this.attentionColumns = this.geocodeService.buildAttentionColumns(locations);
                this.attentionRows = this.geocodeService.buildAttentionRows(
                    locations,
                    this.attentionColumns
                );


                if (emitScreen) {
                    this.screenChanged.emit(
                        this.attentionRows.length > 0
                            ? MaiaScreen.GEOCODING_NEEDS_ATTENTION
                            : MaiaScreen.GEOCODING_COMPLETE
                    );
                }
            },
            error: () => {
                this.isLoadingAttention = false;
                this.attentionColumns = [];
                this.attentionRows = [];
                this.attentionError =
                    "We couldn't load the locations needing attention. Please try again.";

                // The list is what the screen was waiting on, so a failure here
                // must still resolve it - otherwise the user is left on the
                // spinner. Fall back to the streamed count.
                if (emitScreen) {
                    this.screenChanged.emit(
                        this.allResolved
                            ? MaiaScreen.GEOCODING_COMPLETE
                            : MaiaScreen.GEOCODING_NEEDS_ATTENTION
                    );
                }
            },
        });
    }

    /**
     * Persists rows to the server, then reloads the list so the table reflects
     * whatever the update actually resolved.
     */
    /**
     * "Retry and Continue" / "Continue without fixing" — one button, two jobs.
     *
     * Edits live only in the table until something saves them, so leaving with
     * unsaved boxes silently discards them. Anything entered is persisted
     * first and the step advances only once that succeeds; a failed save keeps
     * the user here with the error rather than moving on and losing the work.
     */
    continueWithoutFixing(): void {
        const edited = this.editedRows;

        if (!edited.length) {
            this.continueRequested.emit();
            return;
        }

        this.saveRows(edited, () => this.continueRequested.emit());
    }

    private saveRows(rows: AttentionRow[], onSaved?: () => void): void {
        if (!rows.length || this.jobId === null || this.isSavingRows) {
            return;
        }

        // Validate before the request, not after: a bad coordinate pair would
        // otherwise be rejected by the server with a message that can't point
        // at the offending row.
        if (!this.validateCoordinates(rows)) {
            this.updateError =
                "Some coordinates aren't valid. Check the highlighted rows and try again.";
            return;
        }

        const jobId = this.jobId;
        const payload = this.geocodeService.buildUpdatePayload(this.attentionColumns, rows);

        this.isSavingRows = true;
        this.updateError = "";
        for (const row of rows) {
            row.isSaving = true;
            row.error = null;
        }

        this.updateSubscription = this.geocodeService
            .updateNotFoundLocations(jobId, payload)
            .subscribe({
                next: () => {
                    this.isSavingRows = false;

                    // Leaving the screen: no point refreshing a table that is
                    // about to be torn down.
                    if (onSaved) {
                        onSaved();
                        return;
                    }

                    // Reload rather than mutating locally: the update may have
                    // resolved rows, which drops them off the list entirely —
                    // and that moves the counters too, so refresh both.
                    this.loadCounters(jobId);
                    this.loadAttentionRows(jobId);
                },
                error: (error) => {
                    this.isSavingRows = false;
                    for (const row of rows) {
                        row.isSaving = false;
                    }
                    this.updateError = this.extractHttpMessage(
                        error,
                        "We couldn't save those changes. Please try again."
                    );
                },
            });
    }

    /** "Try again" on the FAILED screen — restarts the whole job. */
    retryJob(): void {
        this.startGeocode();
    }


    private startGeocode(): void {
        this.stopJob();
        this.status = GeocodeStatus.IN_PROGRESS;
        this.progressPercent = 0;
        this.summary = null;
        this.attentionColumns = [];
        this.attentionRows = [];
        this.attentionError = "";
        this.errorMessage = "";

        if (this.jobId === null || this.jobId === undefined) {
            this.status = GeocodeStatus.FAILED;
            this.errorMessage = "Missing upload job. Please go back and re-upload your file.";
            return;
        }

        this.screenChanged.emit(MaiaScreen.GEOCODING_IN_PROGRESS);

        this.jobSubscription = this.geocodeService
            .startGeocode(this.jobId, this.totalLocations)
            .subscribe({
                next: (event) => this.applyEvent(event),
                // Guards against a resumed session landing here for a job whose
                // status no longer accepts a fresh /geocode trigger (e.g. already
                // COMPLETE) — surfaces as FAILED instead of hanging on "Starting…".
                error: () => {
                    this.status = GeocodeStatus.FAILED;
                    this.errorMessage = "Something went wrong while geocoding. Please try again.";
                    this.screenChanged.emit(MaiaScreen.GEOCODING_FAILED);
                },
            });
    }

    private applyEvent(event: GeocodeEvent): void {
        if (event.status === GeocodeStatus.COMPLETE && this.jobId !== null) {
            // status is deliberately not set yet - see
            // showCompleteWhenCountersLand. The stream carries no counts:
            // Preserved / Geocoded / Failed come from the job summary, and the
            // rows needing attention from the not-found list, both server-side
            // facts, neither inferred.
            this.showCompleteWhenCountersLand(this.jobId);
            this.loadAttentionRows(this.jobId, true);
            return;
        }

        this.status = event.status;

        if (event.status === GeocodeStatus.IN_PROGRESS) {
            this.progressPercent = event.percent;
            return;
        }

        if (event.status === GeocodeStatus.COMPLETE) {
            // Only reachable without a jobId, so there is nothing to load and
            // nothing to wait for - the branch above handles every real job.
            this.screenChanged.emit(MaiaScreen.GEOCODING_COMPLETE);
            return;
        }

        this.errorMessage = event.error || "Something went wrong while geocoding. Please try again.";
        this.screenChanged.emit(MaiaScreen.GEOCODING_FAILED);
    }

    private stopJob(): void {
        if (this.jobSubscription) {
            this.jobSubscription.unsubscribe();
            this.jobSubscription = undefined;
        }
    }
}
